import type { Client, ClientStatus } from '@frontend/api-types';
import { convertObjectKeysCase } from '@frontend/duck-tape';
import { AnalyticsBrowser } from '@segment/analytics-next';

export type SegmentUser = {
  anonymousId: () => MaybeUndefined<string>;
};

export type SegmentClient = {
  identify: (userId: string, traits: Record<string, unknown>) => void;
  page: (category?: string, name?: string, properties?: Record<string, unknown>) => void;
  ready: (callback: () => void) => void;
  reset: () => void;
  track: (event: string, properties?: Record<string, unknown>) => void;
  user: () => Promise<SegmentUser>;
};

type MaybePartialSegmentClient = Partial<SegmentClient>;

const requiredSegmentMethods = ['identify', 'track', 'page', 'ready', 'reset', 'user'] as const;
const createUnavailableSegmentUser = () => Promise.resolve({ anonymousId: () => undefined });

// eslint-disable-next-line fp/no-let
let segment: MaybeUndefined<SegmentClient>;

const validateSegmentClient = (client: MaybePartialSegmentClient): SegmentClient => {
  const missingMethods = requiredSegmentMethods.filter((method) => typeof client[method] !== 'function');
  if (missingMethods.length > 0) {
    throw new Error(`Segment loaded but is missing expected methods: ${missingMethods.join(', ')}`);
  }
  return client as SegmentClient;
};

const createUnavailableSegmentClient = (): SegmentClient => ({
  identify: () => undefined,
  page: () => undefined,
  ready: (callback) => callback(),
  reset: () => undefined,
  track: () => undefined,
  user: createUnavailableSegmentUser,
});

const markSegmentUnavailable = (error: unknown) => {
  // eslint-disable-next-line no-console
  console.warn('Failed to load Segment analytics.', error);
  const unavailableSegment = createUnavailableSegmentClient();
  // eslint-disable-next-line fp/no-mutation
  segment = unavailableSegment;
  return unavailableSegment;
};

export const initSegment = ({ writeKey }: { writeKey: string }) => {
  if (segment) {
    // eslint-disable-next-line no-console
    console.warn(
      'Running initSegment a second time, which should not happen. To get the existing instance, use getSegment instead.',
    );
    return segment;
  }

  // .load() should only be called once, according to their docs: https://github.com/segmentio/segment-next/tree/master/packages/browser#readme:~:text=%E2%9A%A0%EF%B8%8F,called%20once.
  try {
    const loadedSegment = validateSegmentClient(AnalyticsBrowser.load({ writeKey }) as MaybePartialSegmentClient);
    // eslint-disable-next-line fp/no-mutation
    segment = loadedSegment;
    return segment;
  } catch (error: unknown) {
    return markSegmentUnavailable(error);
  }
};

export const getSegment = (params?: { required: boolean }): NotNil<typeof segment> => {
  if (!segment && params?.required) {
    // eslint-disable-next-line no-console
    console.warn('Segment has not been initialized yet. Call initSegment() first.');
  }
  // @ts-expect-error We want consumers of this function to always assume it's not undefined
  return segment;
};

export const getAttributionCookies = (): Record<string, string> => {
  const cookies = document.cookie.split(';');
  const getCookieValue = (name: string) => {
    const cookie = cookies.find((c) => c.trim().startsWith(`${name}=`));
    if (!cookie) return undefined;
    return cookie.trim().substring(name.length + 1) || undefined;
  };
  const fbc = getCookieValue('_fbc');
  const fbp = getCookieValue('_fbp');
  const ga = getCookieValue('_ga');
  const gclAw = getCookieValue('_gcl_aw');
  return {
    ...(fbc && { fbc }),
    ...(fbp && { fbp }),
    ...(ga && { ga }),
    ...(gclAw && { gcl_aw: gclAw }),
  };
};

export const getRdtUuid = (): MaybeUndefined<string> => {
  const name = '_rdt_uuid';
  const cookie = document.cookie.split(';').find((c) => c.trim().startsWith(`${name}=`));
  if (!cookie) return undefined;
  return cookie.trim().substring(name.length + 1) || undefined;
};

export const identifySegment = ({
  client,
  clientStatus,
  env,
  fbclid,
  gclid,
}: {
  client: Client;
  clientStatus?: ClientStatus;
  env: EnvType;
  fbclid?: string;
  gclid?: string;
}) => {
  const { createdAt, email, firstName, id, lastName, signupPromoCode } = client;
  const attributionCookies = getAttributionCookies();
  const traits = {
    ...convertObjectKeysCase({ createdAt, email, firstName, lastName }, 'snakeCase'),
    ...(signupPromoCode && { signup_promo_code: signupPromoCode }),
    ...attributionCookies,
    ...(gclid && { gclid }),
    ...(fbclid && { fbclid }),
    ...(clientStatus && {
      has_payment_method: clientStatus.hasPaymentMethod,
      membership_status: clientStatus.membershipStatus,
      onboarding_complete: clientStatus.onboardingComplete,
      super_agent_enrollment_status: clientStatus.superAgentEnrollmentStatus,
    }),
  };
  if (env === 'local') {
    // eslint-disable-next-line no-console
    console.info('Mock Segment identify:', { id, traits });
    return;
  }
  getSegment().identify(id, traits);
  return;
};

export const deidentifySegment = () => {
  getSegment().reset();
  return;
};
